import type { ReactNode } from 'react';
import { readableInk } from '@codezo/brand-detail';
import { Avatar, cn } from './ui/primitives';

/**
 * The page's masthead: a band in the account's colors, with the client's mark
 * lapping over its bottom edge.
 *
 * The band splits into equal hard-edged columns — the same split the brand
 * cards' palette bar uses, and for the same reason. A band that shows every
 * color cannot pick the wrong one; a band that BLENDS them can, and did:
 * interpolating between an account's brands produced a muddy wash whose widest
 * area was a color no brand actually owns.
 *
 * An account has no palette of its own, so the band is its BRANDS'. Give it
 * `brands` and each one gets a named column: its lead color as the fill, its
 * other colors as a strip along the bottom, and its name written on it. Without
 * the names the band is a decorative smear — pretty, and it tells you nothing
 * about which kits this account actually owns.
 *
 * `colors` is the unnamed fallback for a caller that has hexes and no brands;
 * with neither, the band falls back to the app's own accent, so a client with
 * no brand kit still gets a masthead instead of a hole where one should be.
 *
 * A brand that has no palette yet is a different case from a client that has no
 * brands, and looks it: it keeps its named column but wears a plain surface, so
 * it reads as an empty kit rather than borrowing a color it does not own.
 */
export type HeroBrand = {
	name: string;
	/** The brand's palette in display order — the first is the column's fill. */
	colors?: readonly string[] | null;
};

export function ClientHero({
	name,
	code,
	logoUrl,
	brands,
	colors,
	meta,
	badges,
	actions,
	mark,
}: {
	name: string;
	/** Two or three letters for the mark, when there is no logo. */
	code?: string | null;
	logoUrl?: string | null;
	/**
	 * Replaces the mark tile entirely — for an app that lets the viewer CHANGE
	 * the logo, which means an upload trigger, a hover state and a spinner.
	 *
	 * A slot rather than an `onLogoClick` prop because uploading is a whole
	 * flow: pick a file, crop it, PUT two blobs, persist. The app owns all of
	 * it, including whether this viewer may do it at all. The hero keeps the
	 * one thing that is genuinely layout — the tile's size and how it laps over
	 * the band's bottom edge.
	 */
	mark?: ReactNode;
	/** One named column per brand, in display order. Wins over `colors`. */
	brands?: readonly HeroBrand[] | null;
	/** Unnamed fallback: every color becomes an equal segment, in the order given. */
	colors?: readonly string[] | null;
	/** The line under the name — region, industry, "client since". */
	meta?: ReactNode;
	/** Status marks beside the name. */
	badges?: ReactNode;
	actions?: ReactNode;
}) {
	const hasBrands = Boolean(brands && brands.length > 0);
	const hasColors = Boolean(colors && colors.length > 0);

	/* Named columns when there are brands, plain color segments when there are
	   only hexes, one empty column when there is nothing at all. */
	const columns: HeroBrand[] = hasBrands
		? [...brands!]
		: hasColors
			? colors!.map((hex) => ({ name: '', colors: [hex] }))
			: [{ name: '', colors: null }];

	/**
	 * What a column with no color of its own wears.
	 *
	 * Two different situations, and they should not look the same. A client with
	 * no brands at all gets the app's accent, so the masthead is a masthead
	 * rather than a hole. But a brand that simply has no palette YET sits beside
	 * brands that do, and painting it accent would claim a color it doesn't own
	 * — so it gets a plain surface and reads, correctly, as empty.
	 */
	const blank =
		hasBrands || hasColors
			? /* Striped, the same backdrop the image viewer puts behind a photo
			     and `PaletteBar` already puts behind an empty palette. A flat
			     panel here is ambiguous — it could be a column that failed to
			     paint; the stripes say "nothing set" and can't be mistaken for
			     a colour the brand owns. */
				{
					fill: 'var(--surface-fill)',
					ink: 'var(--text-2)',
					striped: true,
					divider: 'var(--line-2)',
				}
			: {
					fill: 'var(--accent)',
					ink: 'var(--accent-fg)',
					striped: false,
					divider: 'rgba(255,255,255,0.3)',
				};

	/**
	 * Fill and ink, decided together.
	 *
	 * They are a PAIR and have to be chosen as one. The bug this replaces picked
	 * them separately — a colourless brand fell back to `--surface-fill` for the
	 * fill but still took `--accent-fg` for the text, and since `--accent-fg` is
	 * the foreground for the ACCENT (near-white on the light theme), the brand's
	 * name came out white on cream and vanished.
	 */
	const paint = (hex?: string | null) => {
		if (!hex) return blank;
		const ink = readableInk(hex);
		return {
			fill: hex,
			ink,
			striped: false,
			/* The divider is drawn INSIDE the column, so it has to be legible on
			   that column's own fill — white-ish over a dark brand, black-ish
			   over a pale one. A single fixed line colour disappears on half the
			   palettes an account can throw at this band. */
			divider: ink === '#FFFFFF' ? 'rgba(255,255,255,0.32)' : 'rgba(0,0,0,0.18)',
		};
	};

	/**
	 * The stripes belong to the BAND, not to each column.
	 *
	 * `repeating-linear-gradient` is laid out from its own element's edge, so
	 * striping each empty column separately restarted the pattern at every
	 * boundary and drew a seam between neighbours — four colourless brands read
	 * as four panels rather than one blank field. Painted once across the band
	 * and left to show through the columns that have no colour of their own, the
	 * diagonal runs unbroken and only the coloured columns interrupt it.
	 */
	const anyStriped = columns.some((column) => paint(column.colors?.[0]).striped);

	/* The mark's fallback tile borrows the first column's paint — both halves of
	   it, which is the other place the old code took the accent's foreground and
	   put it on something that was not the accent. */
	const lead = paint(columns[0].colors?.[0]);

	return (
		<div className="mb-8">
			<div
				className={cn(
					'relative flex h-[9.5rem] overflow-hidden rounded-[var(--r-4)] border border-line-1',
					anyStriped && 'bg-stripes'
				)}
			>
				{/* Equal columns, in the order given — each keeps its own hex all
				    the way to its edge, so nothing blends into a color no brand owns. */}
				{columns.map((column, i) => {
					const { fill, ink, striped, divider } = paint(column.colors?.[0]);
					const rest = (column.colors ?? []).slice(1);
					/* A hairline on every boundary but the band's own left edge, so
					   the columns stay countable when two brands are blank or two
					   sit on near-identical colours. Drawn as an inset shadow rather
					   than a border: a border would take a pixel off the column's
					   width and shift the colour split. */
					const edge = i > 0 ? { boxShadow: `inset 1px 0 0 0 ${divider}` } : undefined;
					return (
						<div
							key={`${column.name}-${fill}-${i}`}
							className="relative h-full min-w-0 flex-1"
							/* Transparent when it has no colour, so the band's own
							   stripes run through it uninterrupted. */
							style={{ ...(striped ? undefined : { background: fill }), ...edge }}
						>
							{column.name && (
								<span
									className="absolute top-3 left-3 right-3 truncate text-xs font-medium tracking-wide uppercase opacity-90"
									style={{ color: ink }}
									title={column.name}
								>
									{column.name}
								</span>
							)}
							{/* The brand's remaining colors, so a column is the whole kit
							    and not just its lead. */}
							{rest.length > 0 && (
								<div className="absolute inset-x-0 bottom-0 flex h-2.5">
									{rest.map((hex, j) => (
										<span
											key={`${hex}-${j}`}
											title={hex.toUpperCase()}
											className="h-full flex-1"
											style={{ background: hex }}
										/>
									))}
								</div>
							)}
						</div>
					);
				})}
				{/* A dot field, so a flat single-color band still reads as a
				    surface rather than a rendering error. */}
				<div
					className="absolute inset-0"
					style={{
						backgroundImage:
							'radial-gradient(rgba(255,255,255,0.07) 0.5px, transparent 0.5px)',
						backgroundSize: '18px 18px',
					}}
				/>
			</div>

			{/* pt-3 is the design's 12px: without it the name starts flush against
			    the band's bottom edge. */}
			<div className="relative z-[1] flex flex-wrap items-end gap-x-5 gap-y-3 px-6 pt-3 pb-1">
				<div className="-mt-11 self-start rounded-[var(--r-5)] bg-canvas p-1.5">
					{mark ??
						(logoUrl ? (
							<img
								src={logoUrl}
								alt={name}
								className="h-[76px] w-[76px] rounded-[var(--r-4)] border border-line-1 bg-surface object-cover"
							/>
						) : (
							<span
								className="flex h-[76px] w-[76px] items-center justify-center rounded-[var(--r-4)] font-mono text-xl"
								style={{ background: lead.fill, color: lead.ink }}
							>
								{code || (
									<Avatar
										name={name}
										size={76}
										className="border-0 bg-transparent"
									/>
								)}
							</span>
						))}
				</div>

				<div className="min-w-[16rem] flex-1 pb-1.5">
					<div className="mb-1.5 flex flex-wrap items-center gap-2.5">
						<h1 className="text-3xl font-medium leading-tight -tracking-[0.02em] text-text-1">
							{name}
						</h1>
						{badges}
					</div>
					{meta && <div className="text-base text-text-3">{meta}</div>}
				</div>

				{actions && (
					<div className="ml-auto flex shrink-0 flex-wrap items-center gap-2 pb-1.5">
						{actions}
					</div>
				)}
			</div>
		</div>
	);
}
